import type { ForwardDealProductBuilderRow, OTCForwardFieldsData } from '@src/types/OTC/otcProductBuilderTypes';
import {
  getPriceSourceFixingsByFullSymbolFromMapping,
  USD_PEGGED_NO_CONVERSION_INDEX,
  type FixingIndexMappingMap,
} from '@src/utils/fixtures/OTC/fixingFeedUtils';
import { AVAILABLE_UNDERLYING_SYMBOLS } from '@src/utils/OTC/otcUtils';
import { EMPTY_OBJECT, OTCSettlementTypeEnum, runValidationV1 } from '@talos/kyoko';
import type { WritableDraft } from 'immer';
import { object, string, type InferType } from 'yup-v1-latest';
import type { BaseProductBuilderSliceStrategy } from './BaseProductBuilderSliceStrategy';
import { FORWARD_PAYOFF_TYPES, OTC_SETTLEMENT_TYPES } from './tokens';
import { positiveNumericString } from './validationUtils';

export const ForwardProductBuilderSliceStrategy: BaseProductBuilderSliceStrategy<ForwardDealProductBuilderRow> = {
  getDefaultValues: (
    existingOTCFieldsForClear?: ForwardDealProductBuilderRow['otcFields']
  ): NonNullable<ForwardDealProductBuilderRow['otcFields']> => {
    const forwardDefaults: OTCForwardFieldsData = {
      productType: 'forward',
      quoteAsset: '',
      deliveryAsset: '',
      unitNotionalAmount: undefined,
      notionalAmount: undefined,
      payoffType: 'Linear',
      settlementType: OTCSettlementTypeEnum.Cash,
      index: '',
    };

    return { ...forwardDefaults, ...existingOTCFieldsForClear };
  },

  buildNoSuffixInstrumentName: (updatedRow: WritableDraft<ForwardDealProductBuilderRow>): string => {
    if (updatedRow && updatedRow.baseAsset && updatedRow.otcFields?.payoffType && updatedRow.otcFields?.quoteAsset) {
      // Include quote asset in instrument name if available
      const tenor = updatedRow.otcFields.tenor;
      let instrument = `FWD-${updatedRow.baseAsset}-${updatedRow.otcFields.quoteAsset}`;
      if (tenor) {
        instrument += `-${tenor}`;
      }
      instrument += `-${updatedRow.otcFields.payoffType.toUpperCase()}`;
      return instrument;
    }
    return '';
  },

  updateGridRowData: (
    state: WritableDraft<ForwardDealProductBuilderRow>,
    rowData: ForwardDealProductBuilderRow,
    fixingIndexMapping?: FixingIndexMappingMap
  ): void => {
    const previousBaseAsset = state.baseAsset;
    state.baseAsset = rowData.baseAsset;
    state.productTemplate = rowData.productTemplate;
    state.uniqueSuffix = rowData.uniqueSuffix;

    const baseAssetChanged = previousBaseAsset !== rowData.baseAsset;

    // When underlying changes for Inverse payoff, set delivery asset to underlying
    if (state.otcFields?.payoffType === 'Inverse' && baseAssetChanged) {
      state.otcFields.deliveryAsset = rowData.baseAsset || undefined;
    }

    // When baseAsset changes, update index if current index is invalid for new baseAsset-quoteAsset pair
    if (baseAssetChanged && state.baseAsset && state.otcFields?.quoteAsset && fixingIndexMapping) {
      // Normalize quote asset for USD/USDT/USDC
      const normalizedQuoteAsset =
        state.otcFields.quoteAsset === 'USDT' || state.otcFields.quoteAsset === 'USDC'
          ? 'USD'
          : state.otcFields.quoteAsset;

      // Get available indexes for the new baseAsset-quoteAsset pair
      const fixings = getPriceSourceFixingsByFullSymbolFromMapping(
        state.baseAsset,
        normalizedQuoteAsset,
        fixingIndexMapping
      );
      const availableIndexes = fixings
        .filter(fixing => fixing.Name !== USD_PEGGED_NO_CONVERSION_INDEX.Name)
        .map(fixing => fixing.Name);

      if (availableIndexes.length > 0) {
        const currentIndex = state.otcFields.index;
        const currentIndexIsValid = currentIndex && availableIndexes.includes(currentIndex);

        // If current index is invalid or missing, select the first available (or single if only one)
        if (!currentIndexIsValid) {
          state.otcFields.index = availableIndexes[0];
        }
      } else {
        // No indexes available, clear it (validation will catch this)
        state.otcFields.index = '';
      }
    }
  },

  updateOTCFields: (
    state: WritableDraft<ForwardDealProductBuilderRow>,
    otcFields: ForwardDealProductBuilderRow['otcFields'],
    fixingIndexMapping?: FixingIndexMappingMap
  ): void => {
    if (otcFields == null) {
      state.otcFields = otcFields;
      return;
    }
    const currentPayoffType = state.otcFields?.payoffType;
    const newPayoffType = otcFields?.payoffType;
    const currentDeliveryAsset = state.otcFields?.deliveryAsset;
    const currentQuoteAsset = state.otcFields?.quoteAsset;
    const newQuoteAsset = otcFields?.quoteAsset;
    const currentIndex = state.otcFields?.index;
    const isPayoffTypeChanging = currentPayoffType !== newPayoffType;
    const isQuoteAssetChanging = currentQuoteAsset !== newQuoteAsset;

    // Shallow-copy payload into draft state. Action payloads can be frozen (e.g. RTK); assigning the
    // same reference then mutating deliveryAsset/index below throws in strict mode.
    state.otcFields = {
      ...otcFields,
      ...(!otcFields.deliveryAsset && currentDeliveryAsset ? { deliveryAsset: currentDeliveryAsset } : {}),
    };

    // Handle payoff type changes
    if (isPayoffTypeChanging) {
      if (newPayoffType === 'Inverse') {
        // When switching to Inverse, set delivery asset to underlying
        state.otcFields.deliveryAsset = state.baseAsset || undefined;
      } else if (newPayoffType === 'Linear' && currentPayoffType === 'Inverse') {
        // Only clear delivery asset when switching FROM Inverse TO Linear
        state.otcFields.deliveryAsset = undefined;
      }
    } else if (newPayoffType === 'Inverse' && !state.otcFields.deliveryAsset && state.baseAsset) {
      // If payoff type is already Inverse and deliveryAsset is missing, set it to baseAsset
      state.otcFields.deliveryAsset = state.baseAsset;
    }

    // Handle index auto-selection when quoteAsset changes or when there's only one available index
    if (state.baseAsset && newQuoteAsset && fixingIndexMapping) {
      // Normalize quote asset for USD/USDT/USDC
      const normalizedQuoteAsset = newQuoteAsset === 'USDT' || newQuoteAsset === 'USDC' ? 'USD' : newQuoteAsset;

      // Get available indexes for the baseAsset-quoteAsset pair
      const fixings = getPriceSourceFixingsByFullSymbolFromMapping(
        state.baseAsset,
        normalizedQuoteAsset,
        fixingIndexMapping
      );
      const availableIndexes = fixings
        .filter(fixing => fixing.Name !== USD_PEGGED_NO_CONVERSION_INDEX.Name)
        .map(fixing => fixing.Name);

      // Auto-select if there's exactly one index available
      if (availableIndexes.length === 1) {
        state.otcFields.index = availableIndexes[0];
      } else if (isQuoteAssetChanging && currentIndex && availableIndexes.length > 0) {
        // If quoteAsset changed and current index is not valid for new quoteAsset, select first available
        const currentIndexIsValid = availableIndexes.includes(currentIndex);
        if (!currentIndexIsValid) {
          state.otcFields.index = availableIndexes[0];
        }
      }
    }
  },

  validateFormState: (
    currentRow: ForwardDealProductBuilderRow,
    fixingIndexMapping?: FixingIndexMappingMap
  ): ReturnType<typeof runValidationV1> => {
    const schema = fixingIndexMapping
      ? createForwardProductBuilderSchema(fixingIndexMapping)
      : forwardProductBuilderSchema;
    return runValidationV1(schema, currentRow);
  },

  validateOTCFields: (
    otcFields: ForwardDealProductBuilderRow['otcFields'],
    fixingIndexMapping?: FixingIndexMappingMap
  ): ReturnType<typeof runValidationV1> => {
    return runValidationV1(forwardOtcFieldsSchema, otcFields ?? EMPTY_OBJECT);
  },
};

/** UI-side validation schema for forward OTC Fields form */
export const forwardOtcFieldsSchema = object().shape({
  productType: string()
    .oneOf(['forward'] as const)
    .required('Product type is required'),
  quoteAsset: string().required('Quote asset is required'),
  // deliveryAsset is optional for all payoff types:
  // - For Inverse: it's derived from baseAsset
  // - For Linear: quoteAsset serves as default when deliveryAsset is not set
  deliveryAsset: string().optional(),
  unitNotionalAmount: positiveNumericString('Unit notional amount'),
  payoffType: string()
    .required('Payoff type is required')
    .oneOf(FORWARD_PAYOFF_TYPES, 'Payoff type must be Linear or Inverse'),
  settlementType: string()
    .required('Settlement type is required')
    .oneOf(OTC_SETTLEMENT_TYPES, `Settlement type must be one of: ${OTC_SETTLEMENT_TYPES.join(', ')}`),
  tenor: string().optional(),
  index: string().required('Index is required'),
});

export const createForwardProductBuilderSchema = (fixingIndexMapping?: FixingIndexMappingMap) =>
  object({
    productTemplate: string().required('Product Template is required'),
    product: string()
      .oneOf(['forward'] as const)
      .required('Product is required'),
    baseAsset: string()
      .required('Underlying is required')
      .oneOf([...AVAILABLE_UNDERLYING_SYMBOLS], 'Invalid underlying symbol'),
    uniqueSuffix: string().nullable(),
    otcFields: forwardOtcFieldsSchema
      .required('OTC fields are required')
      .test(
        'index-valid-for-pair',
        'Index must be valid for the selected Underlying and Quote Asset pair',
        function (otcFields) {
          if (!otcFields || !this.parent.baseAsset || !otcFields.quoteAsset || !otcFields.index) {
            return true;
          }

          const normalizedQuoteAsset =
            otcFields.quoteAsset === 'USDT' || otcFields.quoteAsset === 'USDC' ? 'USD' : otcFields.quoteAsset;

          if (fixingIndexMapping && fixingIndexMapping.size > 0) {
            const fixings = getPriceSourceFixingsByFullSymbolFromMapping(
              this.parent.baseAsset,
              normalizedQuoteAsset,
              fixingIndexMapping
            );
            const availableIndexes = fixings
              .filter(fixing => fixing.Name !== USD_PEGGED_NO_CONVERSION_INDEX.Name)
              .map(fixing => fixing.Name);
            return availableIndexes.includes(otcFields.index);
          }

          return true;
        }
      ),
  });

export const forwardProductBuilderSchema = createForwardProductBuilderSchema();

export type ValidatedForwardRowData = InferType<typeof forwardProductBuilderSchema>;
